import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Media, MediaPickerComponent, PickerMode } from '@gripp/shared-logic';
import { Spacing } from 'libs/shared-ui/src/styles';
import { useState } from 'react';
import { Control, useController, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { Button, Menu, Text } from 'react-native-paper';
import { Colors } from '../../../themes';
import { ControlledTextInput } from '../../forms';
import { ImageData, useTypedMediaItem } from '../../media';

type MediaType = 'image' | 'link';

type AddMediaProps = {
  mediaPicker?: MediaPickerComponent;
  control: Control;
  fieldName: string;
};

export const AddMedia: React.FC<AddMediaProps> = (props) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<MediaType | null>(null);
  const openMenu = () => setVisible(true);
  const closeMenu = () => setVisible(false);

  const mediaValue = useWatch({
    control: props.control,
    name: props.fieldName,
  });

  const {
    field: { onChange },
  } = useController({
    control: props.control,
    name: props.fieldName,
  });

  const handleTypeSelect = (type: MediaType) => {
    setSelectedType(type);
    closeMenu();
  };

  if (mediaValue) return null;

  return (
    <>
      <Menu
        visible={visible}
        onDismiss={closeMenu}
        contentStyle={styles.menuContent}
        anchor={
          <Pressable onPress={openMenu}>
            <View style={styles.anchor}>
              <Text style={{ color: Colors.primary, marginLeft: 8 }}>
                + {t('routines.admin.addMedia')}
              </Text>
            </View>
          </Pressable>
        }
      >
        <AddImage {...props} handleTypeSelect={handleTypeSelect} />
        <Menu.Item
          leadingIcon="link"
          onPress={() => handleTypeSelect('link')}
          title={t('routines.admin.link')}
          style={styles.menuItem}
        />
      </Menu>

      {selectedType === 'link' && (
        <AddLink
          control={props.control}
          fieldName={props.fieldName}
          onAdded={(link) => {
            onChange(link);
            setSelectedType(null);
          }}
          onCanceled={() => setSelectedType(null)}
        />
      )}
    </>
  );
};

const AddImage: React.FC<
  AddMediaProps & {
    handleTypeSelect: (type: MediaType) => void;
  }
> = (props) => {
  const {
    field: { onChange, value },
  } = useController({
    control: props.control,
    name: props.fieldName,
  });
  const { t } = useTranslation();
  const ImagePicker = props?.mediaPicker;
  const { handleSet, handleUploadProgress } =
    useTypedMediaItem<ImageData>(value);
  const onImageAdded = (image: Media) => onChange(image);

  if (!ImagePicker) return null;

  return (
    <ImagePicker
      mode={PickerMode.CAMERA}
      icon={'camera-outline'}
      setMedia={(media: ImageData) => handleSet(media, onImageAdded)}
      setProgress={handleUploadProgress}
      style={{
        height: 24,
        width: 100,
        marginLeft: 8,
        display: 'flex',
      }}
      customTrigger={
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingRight: 12,
            paddingLeft: 12,
            height: 48,
          }}
        >
          <MaterialCommunityIcons
            name={'image-plus'}
            color={Colors.black}
            size={24}
          />
          <Text
            style={{
              marginLeft: 8,
              letterSpacing: 0.15,
              fontWeight: '400',
              lineHeight: 24,
              fontSize: 16,
            }}
          >
            {t('routines.admin.image')}
          </Text>
        </View>
      }
    />
  );
};

const AddLink: React.FC<
  AddMediaProps & { onCanceled: () => void; onAdded: (link: string) => void }
> = (props) => {
  const { t } = useTranslation();
  const [error, setError] = useState('');

  const { control, watch, unregister } = useForm({
    defaultValues: { linkUrlTemp: '' },
  });
  const url = watch('linkUrlTemp');

  const normalizeUrl = (input: string): string => {
    let trimmed = input.trim();
    if (!trimmed) return '';
    if (!/^https?:\/\//i.test(trimmed)) {
      trimmed = 'https://' + trimmed;
    }
    return trimmed;
  };

  const isValidUrl = (input: string): boolean => {
    try {
      const url = new URL(input);
      const hostname = url.hostname;

      const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$/;
      const localhostRegex = /^(localhost|127\.0\.0\.1|192\.\d+\.\d+\.\d+)$/;

      return domainRegex.test(hostname) || localhostRegex.test(hostname);
    } catch {
      return false;
    }
  };

  const handleAddLink = () => {
    const normalized = normalizeUrl(url);
    if (!isValidUrl(normalized)) {
      setError(t('routines.admin.invalidUrl') || 'Invalid URL');
      return;
    }
    setError('');
    props.onAdded(normalized);
    unregister('linkUrlTemp');
  };

  const canSave = Boolean(url?.trim() && !error);

  return (
    <Modal
      visible={true}
      onRequestClose={props.onCanceled}
      transparent={true}
      animationType="slide"
    >
      <View style={styles.contentContainer}>
        <View style={styles.formItem}>
          <Text style={styles.formItemTitle}>
            {t('routines.admin.linkUrl')}
          </Text>

          <ControlledTextInput
            control={control}
            name="linkUrlTemp"
            placeholder={t('routines.admin.linkUrlPlaceholder')}
            onFocus={() => setError('')}
          />

          {!!error && <Text style={styles.errorText}>{error}</Text>}
          <View style={styles.formBtn}>
            <Button
              style={styles.button}
              buttonColor={Colors.white}
              labelStyle={[styles.labelStyle, { color: Colors.primary }]}
              mode="contained"
              onPress={props.onCanceled}
            >
              {t('labels.cancel')}
            </Button>
            <Button
              disabled={!canSave}
              style={styles.button}
              buttonColor={Colors.primary}
              labelStyle={styles.labelStyle}
              mode="contained"
              onPress={handleAddLink}
            >
              {t('routines.admin.linkLabel')}
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  menuContent: {
    backgroundColor: Colors.white,
    flexDirection: 'column',
    flex: 1,
    top: 30,
  },
  anchor: {
    height: 20,
    width: 100,
    marginLeft: 8,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuItem: {
    backgroundColor: Colors.white,
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: Spacing.basePadding.paddingVertical,
    marginBottom: 16,
    width: '100%',
    minWidth: 400,
    maxWidth: 500,
    height: 200,
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 8,
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
    color: Colors.blackText,
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  button: {
    borderRadius: 8,
    width: '100%',
    maxWidth: 156,
    height: 44,
    justifyContent: 'center',
    margin: 'auto',
  },
  labelStyle: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.white,
  },
  errorText: {
    color: Colors.red,
  },
  formBtn: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    marginTop: 16,
  },
});
